import api from './api';
import type {
  DashboardStats,
  AdminUser,
  AdminCategory,
  PaginatedResult,
  UpdateUserRoleRequest,
  BanUserRequest,
  Report,
  ReportStatus,
  ReportType,
  ResolveReportRequest,
  Product,
  CreateProductRequest,
} from '../types';

export const adminService = {
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await api.get<DashboardStats>('/admin/dashboard/stats');
    return response.data;
  },

  async getUsers(page = 1, pageSize = 20, search?: string): Promise<PaginatedResult<AdminUser>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (search) {
      params.append('search', search);
    }

    const response = await api.get<PaginatedResult<AdminUser>>(`/admin/users?${params}`);
    return response.data;
  },

  async updateUserRole(userId: string, role: number): Promise<void> {
    await api.put(`/admin/users/${userId}/role`, { role } as UpdateUserRoleRequest);
  },

  async banUser(userId: string, request: BanUserRequest): Promise<void> {
    await api.post(`/admin/users/${userId}/ban`, request);
  },

  async unbanUser(userId: string): Promise<void> {
    await api.post(`/admin/users/${userId}/unban`);
  },

  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/admin/users/${userId}`);
  },

  async getReports(
    page = 1,
    pageSize = 20,
    status?: ReportStatus,
    type?: ReportType
  ): Promise<PaginatedResult<Report>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (status !== undefined) {
      params.append('status', status.toString());
    }

    if (type !== undefined) {
      params.append('type', type.toString());
    }

    const response = await api.get<PaginatedResult<Report>>(`/admin/reports?${params}`);
    return response.data;
  },

  async resolveReport(id: string, request: ResolveReportRequest): Promise<void> {
    await api.put(`/admin/reports/${id}`, request);
  },

  async deleteReportedContent(id: string): Promise<void> {
    await api.delete(`/admin/reports/${id}/content`);
  },

  async updateProduct(productId: string, data: CreateProductRequest): Promise<Product> {
    const response = await api.put<Product>(`/admin/products/${productId}`, data);
    return response.data;
  },

  async deleteProduct(productId: string): Promise<void> {
    await api.delete(`/admin/products/${productId}`);
  },

  async mergeProducts(sourceId: string, targetId: string): Promise<{ message: string; movedCount: number; droppedCount: number }> {
    const response = await api.post(`/admin/products/${targetId}/merge/${sourceId}`);
    return response.data;
  },

  async getCategories(): Promise<AdminCategory[]> {
    const response = await api.get('/admin/categories');
    return response.data;
  },

  async createCategory(name: string, parentId?: number, emoji?: string): Promise<AdminCategory> {
    const response = await api.post<AdminCategory>('/admin/categories', { name, parentId, emoji });
    return response.data;
  },

  async updateCategory(id: number, name: string, emoji?: string): Promise<AdminCategory> {
    const response = await api.put<AdminCategory>(`/admin/categories/${id}`, { name, emoji });
    return response.data;
  },

  async toggleCategory(id: number): Promise<{ isActive: boolean }> {
    const response = await api.patch<{ isActive: boolean }>(`/admin/categories/${id}/toggle`);
    return response.data;
  },

  async deleteCategory(id: number): Promise<void> {
    await api.delete(`/admin/categories/${id}`);
  },

  async importProductsCsv(file: File, clearExisting = false): Promise<{ deleted: number; imported: number; skipped: number; errorCount: number; errors: string[]; message: string }> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/admin/products/import-csv?clearExisting=${clearExisting}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async importCategoriesTxt(file: File): Promise<{ parentsCreated: number; subcategoriesCreated: number; skipped: number; errorCount: number; errors: string[]; message: string }> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/admin/categories/import-txt', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async seedCategories(): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/admin/categories/seed');
    return response.data;
  },

  async seedSmall(): Promise<void> {
    await api.post('/seed/run');
  },

  async seedLarge(): Promise<void> {
    await api.post('/seed/run-large');
  },

  async clearAndSeedSmall(): Promise<void> {
    await api.post('/seed/clear-and-seed');
  },

  async clearAndSeedLarge(): Promise<void> {
    await api.post('/seed/clear-and-seed-large');
  },
};
